import React, { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import { getCurrentLanguage } from '../utils/translations';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [bounce, setBounce] = useState(false);
  const messagesEndRef = useRef(null);
  const currentLanguage = getCurrentLanguage();

  // Knowledge base about AHB
  const knowledge = {
    fr: {
      greeting: "Bonjour ! 👋 Je suis l'assistant virtuel d'Action-Humanitaire Bénin. Comment puis-je vous aider ?",
      about: "Action-Humanitaire Bénin (AHB) est une ONG à but non lucratif basée à Cotonou, au Bénin. Nous œuvrons pour un développement inclusif, durable et résilient afin d'améliorer les conditions de vie des populations vulnérables.",
      mission: "Notre mission est de contribuer à l'amélioration durable des conditions de vie des populations à travers des actions humanitaires, communautaires et de développement.",
      axes: "Nos 5 axes stratégiques (The High 5) sont :\n1. Protection de l'environnement\n2. Amélioration du cadre de vie\n3. Éducation et renforcement des capacités\n4. Accès à l'eau potable et assainissement\n5. Santé et sécurité alimentaire",
      contact: "Vous pouvez nous contacter :\n📧 Email : contact@ahbenin.org\n📞 Téléphone : +229 01 55 07 81 81\n📍 Adresse : Porto-Novo, Bénin",
      donate: "Pour faire un don, vous pouvez visiter notre page de don sur le site ou nous contacter directement. Chaque contribution compte pour améliorer la vie des communautés !",
      membership: "Pour adhérer à AHB, le montant est de 25 000 FCFA (paiement unique). Vous pouvez remplir le formulaire d'adhésion sur notre site dans la section 'Adhésion'.",
      education: "AHB Education est notre programme international de formation digitale en partenariat avec Million Coders. Il offre des formations en développement web, intelligence artificielle, programmation et compétences numériques.",
      projects: "Nos projets couvrent : l'accès à l'eau potable, la santé et sécurité alimentaire, la protection de l'environnement, l'éducation digitale (AHB Education), et le développement économique.",
      values: "Nos valeurs : solidarité, dignité humaine, transparence et responsabilité.",
      partners: "Nous collaborons avec des partenaires nationaux et internationaux pour renforcer notre impact et promouvoir l'innovation sociale.",
      default: "Je ne suis pas sûr de comprendre votre question. Voici ce que je peux vous aider avec :\n• Informations sur AHB\n• Nos projets et missions\n• Comment nous contacter\n• Comment faire un don\n• Comment adhérer\n• AHB Education\n\nN'hésitez pas à reformuler votre question !"
    },
    en: {
      greeting: "Hello! 👋 I'm the virtual assistant of Action-Humanitaire Bénin. How can I help you?",
      about: "Action-Humanitaire Bénin (AHB) is a non-profit NGO based in Cotonou, Benin. We work for inclusive, sustainable and resilient development to improve living conditions for vulnerable populations.",
      mission: "Our mission is to contribute to the sustainable improvement of living conditions through humanitarian, community and development actions.",
      axes: "Our 5 strategic axes (The High 5) are:\n1. Environmental protection\n2. Improving living conditions\n3. Education and capacity building\n4. Access to clean water and sanitation\n5. Health and food security",
      contact: "You can contact us:\n📧 Email: contact@ahbenin.org\n📞 Phone: +229 01 55 07 81 81\n📍 Address: Porto-Novo, Benin",
      donate: "To make a donation, you can visit our donation page on the website or contact us directly. Every contribution counts to improve community lives!",
      membership: "To join AHB, the fee is 25,000 FCFA (one-time payment). You can fill out the membership form on our website in the 'Membership' section.",
      education: "AHB Education is our international digital training program in partnership with Million Coders. It offers training in web development, artificial intelligence, programming and digital skills.",
      projects: "Our projects cover: access to clean water, health and food security, environmental protection, digital education (AHB Education), and economic development.",
      values: "Our values: solidarity, human dignity, transparency and responsibility.",
      partners: "We collaborate with national and international partners to strengthen our impact and promote social innovation.",
      default: "I'm not sure I understand your question. Here's what I can help you with:\n• Information about AHB\n• Our projects and missions\n• How to contact us\n• How to donate\n• How to join\n• AHB Education\n\nFeel free to rephrase your question!"
    }
  };

  const kb = knowledge[currentLanguage] || knowledge.fr;

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'bot', text: kb.greeting }]);
    }
  }, [isOpen]);

  // Bounce animation every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isOpen) {
        setBounce(true);
        setTimeout(() => setBounce(false), 1000);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Simple keyword matching
  const getResponse = (userMsg) => {
    const msg = userMsg.toLowerCase();

    if (msg.match(/bonjour|salut|hello|hi|hey|bonsoir/)) return kb.greeting;
    if (msg.match(/qui.*(vous|ahb|etes|organisation|ong)|about|à propos|c'est quoi/)) return kb.about;
    if (msg.match(/mission|objectif|but|goal/)) return kb.mission;
    if (msg.match(/axe|strateg|high.?5|domaine|secteur|intervention/)) return kb.axes;
    if (msg.match(/contact|joindre|adresse|email|telephone|phone|appeler/)) return kb.contact;
    if (msg.match(/don|donat|soutenir|support|contribu|aider|help/)) return kb.donate;
    if (msg.match(/adhes|adhér|membre|member|join|rejoindre|inscription|inscrire/)) return kb.membership;
    if (msg.match(/education|formation|digital|million.?coders|cours|training|learn/)) return kb.education;
    if (msg.match(/projet|project|eau|water|santé|health|environnement|environment/)) return kb.projects;
    if (msg.match(/valeur|value|princip|solidar|transparence/)) return kb.values;
    if (msg.match(/partenaire|partner|collabor/)) return kb.partners;
    if (msg.match(/merci|thank/)) return currentLanguage === 'fr' ? "De rien ! N'hésitez pas si vous avez d'autres questions. 😊" : "You're welcome! Don't hesitate if you have other questions. 😊";
    if (msg.match(/au revoir|bye|goodbye|à bientôt/)) return currentLanguage === 'fr' ? "Au revoir ! Merci de votre intérêt pour AHB. À bientôt ! 👋" : "Goodbye! Thank you for your interest in AHB. See you soon! 👋";

    return kb.default;
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setInput('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const response = getResponse(userMessage);
      setMessages(prev => [...prev, { role: 'bot', text: response }]);
      setIsTyping(false);
    }, 800 + Math.random() * 700);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button - Bottom Left */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-8 left-6 z-50 w-14 h-14 rounded-full shadow-xl overflow-hidden border-2 border-white hover:scale-110 transition-transform duration-300 ${bounce ? 'animate-bounce' : ''}`}
        aria-label="Chat"
      >
        <img src="/chat/casque.jpeg" alt="Chat" className="w-full h-full object-cover" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 left-6 z-50 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: '500px', maxHeight: 'calc(100vh - 140px)' }}>

          {/* Header */}
          <div className="bg-gradient-to-r from-[#089556] to-[#07D1EB] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/50">
                <img src="/chat/robot.jpeg" alt="Bot" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">AHB Assistant</p>
                <p className="text-white/80 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
                  {currentLanguage === 'fr' ? 'En ligne' : 'Online'}
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'bot' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1">
                    <img src="/chat/robot.jpeg" alt="Bot" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                  msg.role === 'user'
                    ? 'bg-[#089556] text-white rounded-br-md'
                    : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1">
                  <img src="/chat/robot.jpeg" alt="Bot" className="w-full h-full object-cover animate-pulse" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={currentLanguage === 'fr' ? 'Écrivez votre message...' : 'Type your message...'}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#089556]/30 focus:border-[#089556]"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="w-10 h-10 bg-[#089556] hover:bg-[#0a7c4a] text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
